import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BookOpen, Award, ChevronRight, ArrowLeft, Search, Play, CheckCircle2 } from "lucide-react";
import { DesktopSidebar } from "@/components/DesktopSidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import ProfileIconLink from "@/components/ProfileIconLink";
import { listLessons, type LessonRow } from "@/integrations/backend/lessons";
import { getOnboarding } from "@/integrations/backend/onboarding";
import { CATALOG_STALE_BASE_MS, staleTimeWithJitterFromKey } from "@/lib/query-cache";
import { queryKeys } from "@/lib/query-keys";
import { motivationOptions } from "@/lib/onboarding-options";
import { getVideoThumbnailUrl } from "@/lib/exercise-media";
import { Input } from "@/components/ui/input";
import { ClientNavSpacer } from "@/components/client/ClientNavSpacer";

type Props = { onBack?: () => void };

const ClientLearn = ({ onBack }: Props) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [selectedMotivations, setSelectedMotivations] = useState<string[]>([]);
  const [userMotivations, setUserMotivations] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const lessonsKey = queryKeys.lessons.list(false);
  const lessonsQuery = useQuery({
    queryKey: lessonsKey,
    queryFn: () => listLessons(false),
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...lessonsKey]),
  });
  const onboardingKey = queryKeys.onboarding.me;
  const onboardingQuery = useQuery({
    queryKey: onboardingKey,
    queryFn: () => getOnboarding().catch(() => null),
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...onboardingKey]),
  });
  const lessons = lessonsQuery.data ?? [];
  const loading = lessonsQuery.isPending || onboardingQuery.isPending;

  useEffect(() => {
    const onboarding = onboardingQuery.data;
    const motivations = Array.isArray(onboarding?.motivation) ? onboarding!.motivation! : [];
    setUserMotivations(motivations);
  }, [onboardingQuery.data]);

  // Helper function to get placeholder image
  const getPlaceholderImage = (): string => {
    return "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&q=80&w=920";
  };

  const selectedMotivationSet = useMemo(() => new Set(userMotivations), [userMotivations]);
  const selectedFilterSet = useMemo(() => new Set(selectedMotivations), [selectedMotivations]);

  const sortLessons = (items: LessonRow[]) =>
    [...items].sort((a, b) => {
      if (a.lesson_number !== b.lesson_number) {
        return a.lesson_number - b.lesson_number;
      }
      return a.title.localeCompare(b.title);
    });

  const { pendingLessons, completedLessons } = useMemo(() => {
    const byMotivation =
      selectedFilterSet.size === 0
        ? lessons
        : lessons.filter((l) => (Array.isArray(l.motivations) ? l.motivations : []).some((m) => selectedFilterSet.has(m)));

    const q = searchQuery.trim().toLowerCase();
    const filtered = !q
      ? byMotivation
      : byMotivation.filter((l) => {
          const haystack = `${l.title} ${l.short_description}`.toLowerCase();
          return haystack.includes(q);
        });

    const pending = sortLessons(filtered.filter((l) => !l.is_completed));
    const completed = sortLessons(filtered.filter((l) => l.is_completed));
    return { pendingLessons: pending, completedLessons: completed };
  }, [lessons, selectedFilterSet, searchQuery]);

  const hasVisibleLessons = pendingLessons.length > 0 || completedLessons.length > 0;

  const handleStartLesson = (lesson: LessonRow) => {
    navigate(`/lesson/${lesson.id}`);
  };

  const clearMotivationFilters = () => setSelectedMotivations([]);

  const toggleMotivationFilter = (motivationId: string) => {
    setSelectedMotivations((prev) => {
      const next = new Set(prev);
      if (next.has(motivationId)) next.delete(motivationId);
      else next.add(motivationId);
      return Array.from(next);
    });
  };

  return (
    <div className="min-h-screen gradient-dark">
      <DesktopSidebar activeTab="explore" />
      
      {/* Header */}
      <div className={`bg-gradient-to-r from-dark-bg to-dark-card text-white p-6 pt-8 pb-10 rounded-b-[2.5rem] border-b-2 border-primary/20 ${!isMobile ? "md:ml-16" : ""}`}>
        <div className="flex items-center justify-between mb-3">
          <div>
            {onBack && (
              <Button 
                variant="ghost" 
                size="sm"
                className="bg-white/10 rounded-full h-9 px-3 flex items-center justify-center hover:bg-white/20"
                onClick={onBack}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                <span className="text-sm">Back to Explore</span>
              </Button>
            )}
          </div>
          <ProfileIconLink />
        </div>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-primary" />
            <span>Learning Hub</span>
          </h1>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/60" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search lessons…"
            className="h-11 rounded-full border-white/10 bg-white/10 pl-11 pr-4 text-white placeholder:text-white/60 focus-visible:ring-primary/40"
          />
        </div>
      </div>

      <div className={`px-4 pt-4 space-y-4 sm:px-6 sm:pt-6 sm:space-y-6 ${!isMobile ? "pb-6 md:ml-16" : ""}`}>
        {loading ? (
          <div className="text-center text-white py-12">Loading lessons...</div>
        ) : (
          <>
            <MotivationFilter
              selectedMotivations={selectedMotivations}
              selectedMotivationSet={selectedMotivationSet}
              onToggle={toggleMotivationFilter}
              onClear={clearMotivationFilters}
            />

            <div>
              {hasVisibleLessons ? (
                <div className="space-y-8">
                  {pendingLessons.length > 0 && (
                    <section>
                      <h2 className="text-lg font-semibold mb-4 flex items-center text-white">
                        <Award className="w-5 h-5 mr-2 text-primary" />
                        Continue learning
                      </h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {pendingLessons.map((lesson) => (
                          <LessonCard
                            key={lesson.id}
                            lesson={lesson}
                            onClick={() => handleStartLesson(lesson)}
                            getPlaceholderImage={getPlaceholderImage}
                          />
                        ))}
                      </div>
                    </section>
                  )}

                  {completedLessons.length > 0 && (
                    <section>
                      <h2 className="text-lg font-semibold mb-4 flex items-center text-white">
                        <CheckCircle2 className="w-5 h-5 mr-2 text-green-500" />
                        Completed
                      </h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {completedLessons.map((lesson) => (
                          <LessonCard
                            key={lesson.id}
                            lesson={lesson}
                            onClick={() => handleStartLesson(lesson)}
                            getPlaceholderImage={getPlaceholderImage}
                            dimmed
                          />
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              ) : (
                <div className="text-center text-white py-8">
                  <p className="text-gray-light">
                    {selectedMotivations.length > 0 ? "No lessons match your selected motivations yet." : "No lessons available yet."}
                  </p>
                </div>
              )}
            </div>
          </>
        )}
        {isMobile && <ClientNavSpacer />}
      </div>
    </div>
  );
};

const MotivationFilter = ({
  selectedMotivations,
  selectedMotivationSet,
  onClear,
  onToggle,
}: {
  selectedMotivations: string[];
  selectedMotivationSet: Set<string>;
  onClear: () => void;
  onToggle: (motivationId: string) => void;
}) => {
  const selected = motivationOptions.filter((m) => selectedMotivationSet.has(m.id));
  const unselected = motivationOptions.filter((m) => !selectedMotivationSet.has(m.id));
  const ordered = [...selected, ...unselected];
  const selectedFilterSet = new Set(selectedMotivations);

  return (
    <div className="space-y-3">
      {/* Pillboxes (horizontal scroll) */}
      <div className="-mx-1 overflow-x-auto px-1 pb-1">
        <div className="flex w-max gap-2">
          <button
            type="button"
            className={`flex items-center gap-2 rounded-full border px-3 py-2 text-xs transition-colors ${
              selectedFilterSet.size === 0
                ? "border-primary bg-primary text-black"
                : "border-primary/20 bg-dark-card text-white hover:bg-dark-slate"
            }`}
            onClick={onClear}
            aria-label="Show all lessons"
          >
            <span className="whitespace-nowrap">All</span>
          </button>
          {ordered.map((m) => {
            const isSelected = selectedFilterSet.has(m.id);
            const isUserMotivation = selectedMotivationSet.has(m.id);
            return (
              <button
                key={m.id}
                type="button"
                className={`flex items-center gap-2 rounded-full border px-3 py-2 text-xs transition-colors ${
                  isSelected
                    ? "border-primary bg-primary text-black"
                    : isUserMotivation
                      ? "border-primary/40 bg-primary/10 text-white hover:bg-primary/15"
                      : "border-primary/20 bg-dark-card text-white hover:bg-dark-slate"
                }`}
                onClick={() => onToggle(m.id)}
                aria-label={`Filter by ${m.label}`}
              >
                <span className="text-sm">{m.icon}</span>
                <span className="whitespace-nowrap">{m.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const MotivationCard = ({
  motivationId,
  label,
  icon,
  size,
  onSelect,
}: {
  motivationId: string;
  label: string;
  icon: string;
  size: "large" | "small";
  onSelect: () => void;
}) => {
  const isLarge = size === "large";
  return (
    <Card 
      className={`border border-primary/20 bg-dark-card shadow-card overflow-hidden cursor-pointer hover:shadow-neon-glow transition-all ${
        isLarge ? "min-h-[96px]" : "min-h-[72px]"
      }`}
      onClick={onSelect}
    >
      <div className={`p-4 ${isLarge ? "sm:p-5" : "p-3"} text-white`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`${isLarge ? "text-2xl" : "text-xl"}`}>{icon}</div>
            <div>
              <div className={`${isLarge ? "text-lg" : "text-sm"} font-semibold`}>{label}</div>
              {isLarge && (
                <div className="text-xs text-gray-light">
                  Matches your onboarding motivation
                </div>
              )}
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-primary" />
        </div>
      </div>
    </Card>
  );
};

const LessonCard = ({
  lesson,
  onClick,
  getPlaceholderImage,
  dimmed = false,
}: {
  lesson: LessonRow;
  onClick: () => void;
  getPlaceholderImage: () => string;
  dimmed?: boolean;
}) => {
  const lessonImageSrc = lesson.thumbnail_file?.trim() || null;
  const videoThumbnailSrc = getVideoThumbnailUrl(lesson.video_url);
  const [mediaStage, setMediaStage] = useState<"lesson" | "video" | "fallback">(
    lessonImageSrc ? "lesson" : videoThumbnailSrc ? "video" : "fallback"
  );
  const hasVideo = Boolean(lesson.video_url && lesson.video_url.trim() !== "");

  useEffect(() => {
    setMediaStage(lessonImageSrc ? "lesson" : videoThumbnailSrc ? "video" : "fallback");
  }, [lessonImageSrc, videoThumbnailSrc]);

  const cardImageSrc =
    mediaStage === "lesson"
      ? lessonImageSrc
      : mediaStage === "video"
        ? videoThumbnailSrc
        : getPlaceholderImage();

  return (
    <Card
      className={`border border-primary/20 bg-dark-card shadow-card overflow-hidden cursor-pointer hover:shadow-neon-glow transition-all ${
        dimmed ? "opacity-80" : ""
      }`}
      onClick={onClick}
    >
      <div className="relative h-52 overflow-hidden">
        <img
          src={cardImageSrc || getPlaceholderImage()}
          alt={lesson.title}
          className="absolute inset-0 h-full w-full object-cover"
          onError={() => {
            if (mediaStage === "lesson" && videoThumbnailSrc) {
              setMediaStage("video");
              return;
            }
            if (mediaStage !== "fallback") {
              setMediaStage("fallback");
            }
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-black/20" />
        <div className="absolute top-0 left-0 p-3">
          <Badge
            variant={lesson.is_completed ? "success" : "outline"}
            className={
              lesson.is_completed
                ? "gap-1"
                : "border-white/20 bg-black/35 text-white"
            }
          >
            {lesson.is_completed ? <CheckCircle2 className="h-3.5 w-3.5" /> : null}
            {lesson.is_completed ? "Complete" : "Incomplete"}
          </Badge>
        </div>

        {hasVideo ? (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm ring-1 ring-white/15">
              <Play className="h-6 w-6 translate-x-[1px]" />
            </span>
          </div>
        ) : null}

        <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
          <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
            {lesson.is_completed ? <CheckCircle2 className="h-5 w-5 shrink-0 text-green-500" /> : null}
            <span>{lesson.title}</span>
          </h3>
          <p className="text-sm opacity-80 mb-3 line-clamp-2">{lesson.short_description}</p>
          <div className="flex items-center justify-between gap-2">
            <span
              className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
                lesson.is_completed
                  ? "border-transparent bg-green-500/90 text-white"
                  : "border-white/20 bg-black/35 text-white"
              }`}
            >
              {lesson.is_completed ? "Complete" : "Incomplete"}
            </span>
            <Button
              size="sm"
              variant="ghost"
              className="text-xs flex items-center gap-1 bg-dark-card text-white border border-primary/30 hover:bg-primary hover:text-black"
              onClick={(e) => {
                e.stopPropagation();
                onClick();
              }}
            >
              Start Lesson
              <ChevronRight className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ClientLearn;
